<template>
  <section>
    <el-form
      ref="cpcPaylinksForm"
      :model="cpcPaylinksForm"
      :rules="cpcPaylinksFormRules" 
      label-width="150px"
    >
      <el-form-item
        label="Store"
        prop="selectedStore"
      >
        <gx-select
          v-model="cpcPaylinksForm.selectedStore"
          :show-label="false"
          :options="cpcStores"
          track-by="id"
          label="name"
        >
          <template #selectedLabel="{ option }">
            {{ option.store_name }} ({{ option.start_date }} - {{ option.end_date }})
          </template>
          <template #option="{ option }">
            <div class="d-flex justify-content-space-between">
              <span>{{ option.store_name }} ({{ option.start_date }} - {{ option.end_date }})</span>
            </div>
          </template>
        </gx-select>
      </el-form-item>
      <div v-if="cpcPaylinksForm.selectedStore">
        <el-form-item
          label="CPC U1"
          prop="selectedMarketingU1"
        >
          <gx-select
            v-model="cpcPaylinksForm.selectedMarketingU1"
            :show-label="false"
            :options="cpcPaylinksForm.selectedStore.marketing_u1s"
            track-by="marketing_u1"
            label="name"
          >
            <template #selectedLabel="{ option }">
              {{ option.brand_name }} ({{ option.marketing_u1 }})
            </template>
            <template #option="{ option }">
              <div class="d-flex justify-content-space-between">
                <span>{{ option.brand_name }} ({{ option.marketing_u1 }})</span>
              </div>
            </template>
          </gx-select>
        </el-form-item>
        <el-form-item
          label="Country"
          prop="country"
        >
          <gx-select
            v-model="cpcPaylinksForm.selectedCountry"
            :show-label="false"
            :options="countries"
            track-by="value"
            label="name"
          >
            <template #selectedLabel="{ option }">{{ option.name }}</template>
            <template #option="{ option }">
              <div class="d-flex justify-content-space-between">
                <span>{{ option.name }}</span>
                <span>{{ option.value }}</span>
              </div>
            </template>
          </gx-select>
        </el-form-item>
        <el-form-item prop="pageTitle">
          <span slot="label">
            Page Title
          </span>
          <el-input v-model.trim="cpcPaylinksForm.pageTitle"/>
        </el-form-item>
        <el-form-item prop="bannerImage">
          <span slot="label">
            Banner Image
          </span>
          <el-input v-model.trim="cpcPaylinksForm.bannerImage"/>
        </el-form-item>
        <el-form-item prop="shopNowLink">
          <span slot="label">
            Shop Now Link
          </span>
          <el-input v-model.trim="cpcPaylinksForm.shopNowLink"/>
        </el-form-item>
        <el-form-item prop="productLink1">
          <span slot="label">
            Product Link #1
          </span>
          <el-input v-model.trim="cpcPaylinksForm.productLink1"/>
        </el-form-item>
        <el-form-item prop="productLink2">
          <span slot="label">
            Product Link #2
          </span>
          <el-input v-model.trim="cpcPaylinksForm.productLink2"/>
        </el-form-item>
        <el-form-item prop="productLink3">
          <span slot="label">
            Product Link #3
          </span>
          <el-input v-model.trim="cpcPaylinksForm.productLink3"/>
        </el-form-item>
        <el-form-item prop="productLink4">
          <span slot="label">
            Product Link #4
          </span>
          <el-input v-model.trim="cpcPaylinksForm.productLink4"/>
        </el-form-item>
        <el-form-item prop="productLink5">
          <span slot="label">
            Product Link #5
          </span>
          <el-input v-model.trim="cpcPaylinksForm.productLink5"/>
        </el-form-item>
        <div class="button-group">
          <el-button @click="handleClear">{{ $t('button.clear') }}</el-button>
          <el-button
            type="primary"
            @click="handleGenerate">{{ $t('button.generate') }}</el-button>
        </div>

        <div
          v-if="generatedLongLinks"
          class="links-result-container"
        >
          <h3>
            Generated Paylinks
          </h3>
          <el-input
            v-model="generatedLongLinks"
            type="textarea"
            autosize
            readonly
          />
        </div>

        <div
          v-if="generatedBitlyLinks"
          class="links-result-container"
        >
          <h3>
            Generated Bitly Links
          </h3>
          <el-input
            v-model="generatedBitlyLinks"
            type="textarea"
            autosize
            readonly
          />
          <div class="button-group">  
            <el-button
              v-clipboard:copy="generatedBitlyLinks"
              v-clipboard:success="copySuccessed"
              type="primary"
            >
              {{ copyText }}
            </el-button>
          </div>
        </div>
      </div>
    </el-form>
  </section>
</template>

<script>
import clipboard from '../../directive/clipboard'
import { getCPCList, createBitlyLinks, createLinkMeta } from '../../api/paylinks'
import { GOXIP_PAYLINK_DOMAIN, countryList } from '../../utils/const'

export default {
  name: 'CPCPaylinksGenerator',
  directives: {
    clipboard,
  },
  data() {
    const validateUrl = (options, value, callback) => {
      if (options.required && !value) {
        return callback(new Error(this.$t('error.thisFieldIsRequired')))
      }

      if (value) {
        const domains = value.split('\n')
        const selectedStore = this.cpcPaylinksForm.selectedStore

        if (selectedStore && selectedStore.validation_text) {
          const validationTexts = selectedStore.validation_text.split(',')

          for (const domain of domains) {
            const matchWithStoreDomain = !!validationTexts.find(text => domain.toLowerCase().includes(text.toLowerCase()))

            if (!matchWithStoreDomain) {
              return callback(new Error('The URL does not match the selected store.'))
            }
          }
        }
      }

      return callback()
    }

    return {
      cpcPaylinksForm: {
        selectedStore: null,
        selectedMarketingU1: null,
        selectedCountry: null,
        pageTitle: '',
        bannerImage: '',
        shopNowLink: '',
        productLink1: '',
        productLink2: '',
        productLink3: '',
        productLink4: '',
        productLink5: '',
        shopNowLongLink: '',
        productLongLink1: '',
        productLongLink2: '',
        productLongLink3: '',
        productLongLink4: '',
        productLongLink5: '',
        shopNowBitlyLink: '',
        productBitlyLink1: '',
        productBitlyLink2: '',
        productBitlyLink3: '',
        productBitlyLink4: '',
        productBitlyLink5: '',
      },
      cpcPaylinksFormRules: {
        selectedStore: [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'change' }],
        selectedMarketingU1: [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'change' }],
        selectedCountry: [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'change' }],
        shopNowLink: [{ required: true, validator: validateUrl, trigger: 'blur' }],
        productLink1: [{ required: true, validator: validateUrl, trigger: 'blur' }],
        productLink2: [{ required: true, validator: validateUrl, trigger: 'blur' }],
        productLink3: [{ required: true, validator: validateUrl, trigger: 'blur' }],
        productLink4: [{ required: false, validator: validateUrl, trigger: 'blur' }],
        productLink5: [{ required: false, validator: validateUrl, trigger: 'blur' }],
      },
      cpcStores: [],
      countries: countryList,
      generatedLongLinks: null,
      generatedBitlyLinks: null,
      copyText: 'Copy',
    }
  },
  async beforeRouteEnter(to, from, next) {
    try {
      const response = await getCPCList()
      next((vm) => { vm._data.cpcStores = response.data }) // eslint-disable-line no-underscore-dangle
    } catch (error) {
      next(new Error('Unable to get CPC list.'))
    }
  },
  methods: {
    async generateBitlyLinks(longLinks) {
      const response = await createBitlyLinks(longLinks)

      const labels = [
        'Shop Now Link:',
        'Product Link #1:',
        'Product Link #2:',
        'Product Link #3:',
        'Product Link #4:',
        'Product Link #5:',
      ]

      const rows = []

      if (labels.length >= response.data.length) {
        for (let i = 0; i < response.data.length; i++) {
          rows.push(labels[i] + '\n')

          const bitlyUrl = response.data[i].bitly_url

          if (bitlyUrl === null || bitlyUrl === '') {
            rows.push('N/A\n\n')
          } else {
            rows.push(bitlyUrl + '\n\n')
          }
        }

        this.generatedBitlyLinks = rows.join('')
      } else {
        this.$alert(`Error: `, this.$t('error.somethingWentWrong'), {
          type: 'error',
          confirmButtonText: 'OK',
        })
      }      
    },
    generatePaylink(destinationUrl) {
      const selectedStore = this.cpcPaylinksForm.selectedStore
      const countryCode = this.cpcPaylinksForm.selectedCountry.value.toLowerCase()
      const affiliate = selectedStore.affiliate.toLowerCase()
      const advertiserId = selectedStore.deeplink_advertiser_id
      const u1 = `${countryCode}|||${this.cpcPaylinksForm.selectedMarketingU1.marketing_u1}`

      return `${GOXIP_PAYLINK_DOMAIN}/${countryCode}/affiliate?url=${encodeURIComponent(destinationUrl)}&u1=${u1}&affiliate=${affiliate}&advertiser_id=${advertiserId}`
    },
    handleClear() {
      this.$refs.cpcPaylinksForm.resetFields()
      this.generatedLongLinks = null
      this.generatedBitlyLinks = null
    },
    handleGenerate() {
      this.$refs.cpcPaylinksForm.validate((valid) => {
        if (valid) {
          let longLinks = []
          this.generatedLongLinks = null
          this.generatedBitlyLinks = null

          longLinks.push(this.generatePaylink(this.cpcPaylinksForm.shopNowLink))
          longLinks.push(this.generatePaylink(this.cpcPaylinksForm.productLink1))
          longLinks.push(this.generatePaylink(this.cpcPaylinksForm.productLink2))
          longLinks.push(this.generatePaylink(this.cpcPaylinksForm.productLink3))

          if (this.cpcPaylinksForm.productLink4.length > 0) {
            longLinks.push(this.generatePaylink(this.cpcPaylinksForm.productLink4))
          }
          
          if (this.cpcPaylinksForm.productLink5.length > 0) {
            longLinks.push(this.generatePaylink(this.cpcPaylinksForm.productLink5))
          }

          if (longLinks.length >= 4) {
            const title = this.cpcPaylinksForm.pageTitle
            const bannerImage = this.cpcPaylinksForm.bannerImage

            if (title.length > 0 || bannerImage.length > 0) {
              createLinkMeta(title, bannerImage).then((response) => {
                if (response.data && response.data.hash) {
                  longLinks[0] = `${longLinks[0]}&hash=${response.data.hash}`
                }

                this.generatedLongLinks = longLinks.join('\n\n')
                this.generateBitlyLinks(longLinks)
              })
            } else {
              this.generatedLongLinks = longLinks.join('\n\n')
              this.generateBitlyLinks(longLinks)
            }
          }
        } else {
          return false
        }

        return undefined
      })
    },
    copySuccessed() {
      this.copyText = 'Copied'
      setTimeout(() => {
        this.copyText = 'Copy'
      }, 3000)
    },
  }
}
</script>

<style lang="scss" scoped>

section {
  width: 100%;
  max-width: 650px;
  border: $border-base;
  border-radius: $border-radius-base;
  margin: 20px auto;
  padding: 10px;

  button {
    margin: 5px auto;
    width: 40%;
  }

  .el-form {
    .el-select {
      width: 100%;
    }
  }

  .links-result-container {
    display: flex;
    flex-flow: column;
    border-top: $border-base;
    margin-top: 10px;
    h3 {
      text-align: center;
    }
    button {
      margin-top: 15px;
    }
  }

  .button-group {
    display: flex;
  }
}

</style>